import React from 'react'
import { ActivityIndicator, Platform, ScrollView, StyleSheet, View } from 'react-native'
import { useTheme } from '@react-navigation/native'
import type { NativeStackScreenProps } from '@react-navigation/native-stack'
import { Header, Icon, Screen, Text } from '@components'
import { useLanguage } from '@context'
import { Theme } from '@theme'
import { formatEventPrice } from '@utils'
import { type OrganizerStatsResponse, useOrganizerStats } from '@hooks'
import type { EventsStackParamList } from '../../../navigation/navigator/EventsStackNavigator'

type Props = NativeStackScreenProps<EventsStackParamList, 'OrganizerStats'>

interface StatTile {
  label: string
  value: string
  color: string
  testID?: string
}

function buildTiles(
  data: OrganizerStatsResponse,
  t: ReturnType<typeof useLanguage>['t'],
  theme: Theme,
  currentLanguage: string,
): StatTile[] {
  return [
    {
      label: t('events.myStats.events'),
      value: String(data.eventsCount),
      color: theme.colors.primary,
    },
    {
      label: t('events.myStats.registered'),
      value: String(data.attendeeCount),
      color: theme.colors.primary,
    },
    {
      label: t('events.myStats.present'),
      value: String(data.checkedInCount),
      color: theme.colors.green,
      testID: 'stat-present',
    },
    {
      label: t('events.myStats.attendanceRate'),
      value: `${data.attendanceRate}%`,
      color: theme.colors.green,
      testID: 'stat-attendance-rate',
    },
    {
      label: t('events.myStats.contactsExchanged'),
      value: String(data.contactsExchanged),
      color: theme.colors.secondary,
      testID: 'stat-contacts-exchanged',
    },
    {
      label: t('events.myStats.followupsSent'),
      value: String(data.followupsSent),
      color: theme.colors.orange,
    },
    {
      label: t('events.myStats.emailsSent'),
      value: String(data.emailsSent),
      color: theme.colors.orange,
    },
    {
      label: t('events.myStats.collected'),
      value: formatEventPrice(data.collectedCents, currentLanguage),
      color: theme.colors.green,
    },
    {
      label: t('events.myStats.commission'),
      value: formatEventPrice(data.commissionCents, currentLanguage),
      color: theme.colors.orange,
    },
    {
      label: t('events.myStats.net'),
      value: formatEventPrice(data.organizerNetCents, currentLanguage),
      color: theme.colors.green,
    },
  ]
}

/** "Mes statistiques": one aggregated view across every event the organizer runs. */
export const OrganizerStats: React.FC<Props> = ({ navigation }) => {
  const theme = useTheme()
  const { t, currentLanguage } = useLanguage()
  const styles = getStyles(theme)
  const { data, isLoading } = useOrganizerStats(true)

  const tiles = data ? buildTiles(data, t, theme, currentLanguage) : []
  const maxCount = Math.max(1, ...(data?.contactsExchangedLast7Days.map((day) => day.count) ?? [0]))
  const hasCurveData = (data?.contactsExchangedLast7Days ?? []).some((day) => day.count > 0)

  return (
    <Screen>
      <Header
        title={t('events.myStats.title')}
        iconLeft={Platform.OS === 'web' ? 'arrowLeft' : 'arrowBack'}
        onPressLeft={() => navigation.navigate('EventsList')}
      />
      <ScrollView contentContainerStyle={styles.scroll}>
        <View style={styles.content}>
          <Text type="bodySmall" color={theme.colors.greyBlue}>
            {t('events.myStats.subtitle')}
          </Text>

          {isLoading ? (
            <ActivityIndicator color={theme.colors.primary} />
          ) : !data || data.eventsCount === 0 ? (
            <View style={styles.empty}>
              <Icon name="croissance" size={48} color={theme.colors.primary} />
              <Text type="body" color={theme.colors.greyBlue} center>
                {t('events.myStats.empty')}
              </Text>
            </View>
          ) : (
            <>
              <View style={styles.stats}>
                {tiles.map((tile) => (
                  <View key={tile.label} style={styles.stat} testID={tile.testID}>
                    <Text type="bodySmallBold" color={theme.colors.greyBlue}>
                      {tile.label}
                    </Text>
                    <Text type="titleBigBold" color={tile.color}>
                      {tile.value}
                    </Text>
                  </View>
                ))}
              </View>

              <View style={styles.curveCard} testID="contacts-curve">
                <Text type="titleBold">{t('events.myStats.curveTitle')}</Text>
                {hasCurveData ? (
                  <View style={styles.curve}>
                    {(data?.contactsExchangedLast7Days ?? []).map((day) => (
                      <View key={day.date} style={styles.curveColumn}>
                        <Text type="bodyExtraSmallBold" color={theme.colors.secondary}>
                          {day.count}
                        </Text>
                        <View
                          style={[
                            styles.bar,
                            {
                              height: Math.max(4, (day.count / maxCount) * 96),
                              backgroundColor: theme.colors.primary,
                            },
                          ]}
                        />
                        <Text type="bodyExtraSmall" color={theme.colors.greyBlue}>
                          {new Intl.DateTimeFormat(
                            currentLanguage === 'en'
                              ? 'en-GB'
                              : currentLanguage === 'es'
                                ? 'es-ES'
                                : 'fr-FR',
                            { weekday: 'short' },
                          )
                            .format(new Date(`${day.date}T12:00:00`))
                            .replace('.', '')}
                        </Text>
                      </View>
                    ))}
                  </View>
                ) : (
                  <Text type="body" color={theme.colors.greyBlue}>
                    {t('events.myStats.curveEmpty')}
                  </Text>
                )}
              </View>
            </>
          )}
        </View>
      </ScrollView>
    </Screen>
  )
}

const getStyles = (theme: Theme) =>
  StyleSheet.create({
    scroll: { padding: theme.spacing.xl, alignItems: 'center' },
    content: { width: '100%', maxWidth: 880, gap: theme.spacing.lg },
    empty: { alignItems: 'center', gap: theme.spacing.sm, paddingVertical: theme.spacing.xxxl * 2 },
    stats: { flexDirection: 'row', gap: theme.spacing.md, flexWrap: 'wrap' },
    stat: {
      flex: 1,
      minWidth: 160,
      padding: theme.spacing.lg,
      borderRadius: theme.border.large,
      backgroundColor: theme.colors.white,
      borderWidth: 1,
      borderColor: theme.colors.grey,
      gap: 2,
    },
    curveCard: {
      backgroundColor: theme.colors.white,
      borderRadius: theme.border.jumbo,
      borderWidth: 1,
      borderColor: theme.colors.grey,
      padding: theme.spacing.xl,
      gap: theme.spacing.lg,
    },
    curve: {
      flexDirection: 'row',
      alignItems: 'flex-end',
      justifyContent: 'space-between',
      gap: theme.spacing.sm,
      height: 140,
    },
    curveColumn: {
      flex: 1,
      alignItems: 'center',
      gap: theme.spacing.xs,
      justifyContent: 'flex-end',
    },
    bar: { width: '60%', borderRadius: theme.border.small, minHeight: 4 },
  })
